import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Briefcase, Users, Target, Heart, Award, Eye, Shield, CheckCircle2, ArrowRight, Phone, Mail } from 'lucide-react';

const RecrutementPage = () => {
  const [showEmployerMessage, setShowEmployerMessage] = useState(false);
  const whatWeDo = [
    {
      icon: <Briefcase className="w-12 h-12 text-gold" />,
      title: 'Recrutement sur mesure',
      description: 'Spécialisation en assurance de dommages. Nous ciblons des profils spécialisés correspondant aux besoins de chaque employeur.',
    },
    {
      icon: <Users className="w-12 h-12 text-gold" />,
      title: 'Accompagnement des talents',
      description: 'Un suivi personnalisé pour révéler votre plein potentiel et vous connecter aux bonnes opportunités.',
    },
    {
      icon: <Target className="w-12 h-12 text-gold" />,
      title: 'Conseil RH',
      description: 'Des stratégies adaptées à vos enjeux organisationnels, incluant des ressources et des rencontres personnalisées.',
    },
  ];

  const whyChooseUs = [
    {
      icon: <Heart className="w-6 h-6 text-gold" />,
      title: 'Une approche centrée sur l\'humain',
    },
    {
      icon: <Award className="w-6 h-6 text-gold" />,
      title: 'Une expertise sectorielle reconnue',
    },
    {
      icon: <Eye className="w-6 h-6 text-gold" />,
      title: 'Une écoute authentique et stratégique',
    },
    {
      icon: <CheckCircle2 className="w-6 h-6 text-gold" />,
      title: 'Un réseau de professionnels qualifiés et engagés',
    },
  ];

  return (
    <div className="pt-16" data-testid="recrutement-page">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center justify-center bg-[#1a1a1a] overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold text-white mb-6">
            Recrutement
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
            Chasse de têtes spécialisée en assurance de dommages
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xl text-gray-700 leading-relaxed mb-6">
            Nous connectons les bons talents aux bonnes entreprises. Pas pour remplir des postes.
          </p>
          <p className="text-xl text-gray-700 leading-relaxed">
            Pour bâtir des collaborations solides, sincères et porteuses de sens.
          </p>
        </div>
      </section>

      {/* Ce que nous faisons */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
              Ce que nous faisons
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whatWeDo.map((item) => (
              <Card key={item.title} className="border-0 shadow-xl bg-white hover:shadow-2xl">
                <CardContent className="p-10 text-center">
                  <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gold/10 mb-6">
                    {item.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-24 bg-[#1a1a1a] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <Shield className="w-10 h-10 text-gold" />
                <h2 className="text-3xl md:text-4xl font-display font-bold">
                  Notre expertise
                </h2>
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold mb-6 text-gold">
                Assurance de dommages
              </h3>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                Nous comprenons les enjeux spécifiques de votre secteur : complexité des risques, pression du service client, évolution des normes.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                Nous recrutons des experts rigoureux et humains — prêts à relever les défis d'un secteur essentiel à la société.
              </p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {['Souscripteurs', 'Courtiers / Agents', 'Gestionnaires de compte', 'Analystes', 'Experts en sinistre', 'Formateurs', 'Développement des affaires', 'Directeurs'].map((role) => (
                <div key={role} className="bg-white/5 p-6 rounded-xl text-center hover:bg-white/10 transition-all border border-gold/20">
                  <p className="font-bold text-lg text-gold">{role}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi choisir Nexor */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4">
              Pourquoi choisir Nexor Talents ?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {whyChooseUs.map((item) => (
              <div key={item.title} className="flex items-center space-x-4 p-6 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all border-l-4 border-gold">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center">
                    {item.icon}
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {item.title}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
            Prêt à passer à l'action ?
          </h2>
          <p className="text-xl text-gray-700 mb-10 leading-relaxed">
            Que vous soyez candidat ou employeur, nous sommes là pour vous accompagner.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/soumettre-cv">
              <Button size="lg" className="bg-gold hover:bg-[#9a7109] text-white text-lg px-10 py-7 font-semibold">
                Je recherche un emploi - soumettre mon CV
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Button
              size="lg"
              variant="outline"
              onClick={() => setShowEmployerMessage(!showEmployerMessage)}
              data-testid="employer-btn"
              className="border-2 border-gold text-gold hover:bg-gold hover:text-white text-lg px-10 py-7 font-semibold"
            >
              Je suis un employeur
            </Button>
          </div>

          {showEmployerMessage && (
            <div className="mt-8 max-w-2xl mx-auto p-6 bg-[#1a1a1a] rounded-xl border border-gold/30 text-left" data-testid="employer-message">
              <p className="text-white text-lg mb-4">
                Vous cherchez à renforcer votre équipe ? Contactez-nous directement afin de discuter de vos besoins.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="tel:5145453383" className="inline-flex items-center gap-2 px-5 py-3 bg-gold hover:bg-[#9a7109] text-white font-semibold rounded-lg transition-colors">
                  <Phone className="w-4 h-4" />
                  514-545-3383
                </a>
                <a href="mailto:info@nexortalents.com" className="inline-flex items-center gap-2 px-5 py-3 border-2 border-gold text-gold hover:bg-gold hover:text-white font-semibold rounded-lg transition-colors">
                  <Mail className="w-4 h-4" />
                  info@nexortalents.com
                </a>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default RecrutementPage;
